import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

import type { ClientDebtHistoryData, ClientDebtHistoryEntry } from "../types";

interface UseClientDebtHistoryParams {
  clientId: string | undefined;
  startDate: string | null;
  endDate: string | null;
}

export const useClientDebtHistory = ({
  clientId,
  startDate,
  endDate,
}: UseClientDebtHistoryParams) => {
  return useQuery<ClientDebtHistoryData>({
    queryKey: ["client-debt-history", clientId, startDate, endDate],
    queryFn: async () => {
      if (!clientId) {
        return { entries: [], clientInfo: null };
      }

      let query = supabase
        .from("daily_client_debt_details")
        .select("*")
        .eq("client_id", clientId)
        .order("snapshot_date", { ascending: false });

      if (startDate) {
        query = query.gte("snapshot_date", startDate);
      }

      if (endDate) {
        query = query.lte("snapshot_date", endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      const entries = (data || []) as ClientDebtHistoryEntry[];

      // Get client info from the first entry
      const clientInfo =
        entries.length > 0
          ? {
              client_id: entries[0].client_id,
              client_name: entries[0].client_name,
              client_rut: entries[0].client_rut,
            }
          : null;

      return {
        entries,
        clientInfo,
      };
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
};
